import { apiClient } from './apiClient';
import { authService } from './authService';

const getCompanyParam = () => {
  const companyId = authService.getCompanyId();
  return companyId ? `companyId=${companyId}` : '';
};

export const chatbotService = {
  // Chatbot CRUD
  async getChatbots() {
    return apiClient.get(`/api/chatbots?${getCompanyParam()}`);
  },

  async getChatbot(id) {
    return apiClient.get(`/api/chatbots/${id}?${getCompanyParam()}`);
  },

  async createChatbot(data) {
    return apiClient.post(`/api/chatbots?${getCompanyParam()}`, data);
  },

  async updateChatbot(id, data) {
    return apiClient.put(`/api/chatbots/${id}?${getCompanyParam()}`, data);
  },

  async deleteChatbot(id) {
    return apiClient.delete(`/api/chatbots/${id}?${getCompanyParam()}`);
  },

  async generateApiKey(chatbotId) {
    return apiClient.post(`/api/chatbots/${chatbotId}/api-key?${getCompanyParam()}`);
  },

  async revokeApiKey(chatbotId) {
    return apiClient.delete(`/api/chatbots/${chatbotId}/api-key?${getCompanyParam()}`);
  },

  async uploadAvatar(chatbotId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.upload(`/api/chatbots/${chatbotId}/avatar?${getCompanyParam()}`, formData);
  },

  // Documents
  async getDocuments(chatbotId) {
    return apiClient.get(`/api/documents/chatbot/${chatbotId}`);
  },

  async uploadDocument(chatbotId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.upload(`/api/documents/upload?chatbotId=${chatbotId}`, formData);
  },

  async deleteDocument(documentId) {
    return apiClient.delete(`/api/documents/${documentId}`);
  },

  async reprocessDocument(documentId) {
    return apiClient.post(`/api/documents/${documentId}/reprocess`);
  },

  async addUrlSource(chatbotId, url) {
    return apiClient.post(`/api/documents/url?chatbotId=${chatbotId}&url=${encodeURIComponent(url)}`);
  },

  async getDocumentQuota(chatbotId) {
    return apiClient.get(`/api/documents/chatbot/${chatbotId}/quota`);
  },

  // Conversations
  async getConversations(chatbotId, page = 0, size = 20) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('size', size);
    if (chatbotId) params.append('chatbotId', chatbotId);
    return apiClient.get(`/api/chatbots/conversations?${getCompanyParam()}&${params.toString()}`);
  },

  async getConversationMessages(conversationId) {
    return apiClient.get(`/api/chatbots/conversations/${conversationId}/messages`);
  },

  // Dashboard stats
  async getDashboardStats() {
    return apiClient.get(`/api/chatbots/stats?${getCompanyParam()}`);
  },

  // Analytics (period: '7days' | '30days' | '90days')
  async getAnalytics(period = '30days') {
    return apiClient.get(`/api/chatbots/analytics?period=${period}&${getCompanyParam()}`);
  },

  // Agent management
  async getAgents() {
    return apiClient.get(`/api/chatbot/agents?${getCompanyParam()}`);
  },

  async createAgent(data) {
    return apiClient.post(`/api/chatbot/agents?${getCompanyParam()}`, data);
  },

  async updateAgent(agentId, data) {
    return apiClient.put(`/api/chatbot/agents/${agentId}?${getCompanyParam()}`, data);
  },

  async deactivateAgent(agentId) {
    return apiClient.delete(`/api/chatbot/agents/${agentId}?${getCompanyParam()}`);
  },

  async activateAgent(agentId) {
    return apiClient.put(`/api/chatbot/agents/${agentId}/activate?${getCompanyParam()}`);
  },

  // Agent seat usage from subscription service
  async getAgentUsage() {
    const companyId = authService.getCompanyId();
    return apiClient.get(`/api/subscriptions/chatbot/company/${companyId}/agent-usage`);
  },

  async purchaseAgentSlots(quantity) {
    const companyId = authService.getCompanyId();
    return apiClient.post(`/api/subscriptions/chatbot/${companyId}/extra-agents?quantity=${quantity}`);
  },
};

export const chatbotAdminService = {
  getCompanyHandledConversations(companyId, page = 0, size = 20, agentId = null, status = null, search = null) {
    const params = new URLSearchParams({ companyId, page, size });
    if (agentId) params.append('agentId', agentId);
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    return apiClient.get(`/api/chatbot/agents/analytics/conversations?${params.toString()}`);
  },

  getCompanySummary(companyId) {
    return apiClient.get(`/api/chatbot/agents/analytics/summary?companyId=${companyId}`);
  },

  getAgentsList(companyId) {
    return apiClient.get(`/api/chatbot/agents?companyId=${companyId}`);
  },

  getConversationMessages(sessionId) {
    return apiClient.get(`/api/chatbot/agents/analytics/conversations/${encodeURIComponent(sessionId)}/messages`);
  },

  addCallbackUpdate(sessionId, note) {
    const u = authService.getCurrentUser();
    const authorName = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || null : null;
    return apiClient.post(`/api/chatbot/agents/analytics/conversations/${encodeURIComponent(sessionId)}/callback-update`, { note, authorName });
  },

  closeCallbackConversation(sessionId, note, status) {
    const u = authService.getCurrentUser();
    const authorName = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || null : null;
    return apiClient.post(`/api/chatbot/agents/analytics/conversations/${encodeURIComponent(sessionId)}/close-callback`, { note, status, authorName });
  },
};
